/**
 * ML-DSA-65 (NIST FIPS-204 final) signing-leg FACADE. This module is the
 * STATIC entry point: it carries the wire-format constants + the
 * `MlDsaSigner` type + a lazy-load surface, but holds NO
 * `@noble/post-quantum` import. The ~24 KB post-quantum implementation
 * lives in `crypto/ml_dsa_impl.ts` and is reached ONLY via a dynamic
 * `import("./ml_dsa_impl")`, so Rollup splits it into a lazy async chunk
 * that an ed25519-only account never loads.
 *
 * Loading model:
 *   - `preloadMlDsa()` awaits the dynamic import once + caches the module
 *     reference. Called at boot (`main.tsx`) when the restored session
 *     carries a hybrid ml-dsa seed (`restored.mlDsaSeed != null`) — the
 *     ONLY transition that sets a non-null seed. ed25519-only accounts
 *     never call it, so the chunk never loads on the live path.
 *   - `getCachedDeriveMlDsa()` / `getCachedDeriveMlDsaPublicKey()` return
 *     the already-loaded implementation synchronously, so the SYNCHRONOUS
 *     signing paths (`signAndEncodeAction`, the two HTTP header builders,
 *     the WS handshake message listener) keep their sync API. They throw
 *     if the module was not preloaded — which can only happen on a hybrid
 *     account whose boot preload was skipped, never on the ed25519 path.
 *   - Genuinely-async call sites (`auth/recovery.ts` enroll) can await the
 *     dynamic import inline instead of preloading.
 */

/** FIPS-204 ML-DSA-65 fixed encoded sizes (§4). */
export const ML_DSA_65_VK_LEN = 1952;
export const ML_DSA_65_SIG_LEN = 3309;

export interface MlDsaSigner {
  /** 1952-byte FIPS-204 encoded verifying key. */
  readonly publicKey: Uint8Array;
  /**
   * Sign a 32-byte canonical message. `hedged: true` (the default) uses
   * `extraEntropy: true` (FIPS-204 hedged / randomized) which is fine for
   * the live signer; tests that need byte-reproducibility against the
   * deterministic Rust fixture pass `hedged: false`.
   */
  sign(message: Uint8Array, opts?: { hedged?: boolean }): Uint8Array;
}

/** Shape of the dynamically-imported implementation leaf. */
type MlDsaImpl = typeof import("./ml_dsa_impl");

/** Module-level cache for the lazily-imported `@noble/post-quantum` leaf.
 *  `null` until `preloadMlDsa()` (or an inline dynamic import) resolves. */
let cachedMod: MlDsaImpl | null = null;

/**
 * Await the dynamic import of the post-quantum leaf once + cache the
 * module reference. Idempotent — a second call returns the cached module
 * without re-importing. Call at boot when the restored session carries a
 * hybrid ml-dsa seed, BEFORE the first synchronous sign path runs.
 */
export async function preloadMlDsa(): Promise<MlDsaImpl> {
  if (cachedMod === null) {
    cachedMod = await import("./ml_dsa_impl");
  }
  return cachedMod;
}

/** Whether the post-quantum leaf has been preloaded (test/diagnostic). */
export function isMlDsaPreloaded(): boolean {
  return cachedMod !== null;
}

function requireCachedMod(): MlDsaImpl {
  if (cachedMod === null) {
    throw new Error(
      "ml-dsa not preloaded: call preloadMlDsa() before a synchronous hybrid sign",
    );
  }
  return cachedMod;
}

/**
 * Synchronous accessor for the cached `deriveMlDsa`. The synchronous
 * signing paths use this so their API stays sync; throws when the leaf was
 * not preloaded (only reachable on a hybrid account whose boot preload was
 * skipped — never on the ed25519-only live path).
 */
export function getCachedDeriveMlDsa(): MlDsaImpl["deriveMlDsa"] {
  return requireCachedMod().deriveMlDsa;
}

/** Synchronous accessor for the cached `deriveMlDsaPublicKey`. */
export function getCachedDeriveMlDsaPublicKey(): MlDsaImpl["deriveMlDsaPublicKey"] {
  return requireCachedMod().deriveMlDsaPublicKey;
}
